import React, { useState, useMemo, useEffect } from 'react';
import DynamicStepCard from 'design-system/src/components/DynamicStepCard/DynamicStepCard';
import type { ButtonProps, RouterLinkProps } from 'design-system/src/components/Button/Button';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { useNavigate } from 'react-router-dom';

import gradientPrescriptionIcon from 'design-system/src/assets/icons/gradientRx.png';
import gradientProviderIcon from 'design-system/src/assets/icons/gradientProvider.png';
import gradientOfficeIcon from 'design-system/src/assets/icons/gradientOffice.png';
import gradientPeopleIcon from 'design-system/src/assets/icons/gradientPeople.png';
import { useQuery } from '@tanstack/react-query';
import { client } from '../../api';
import type { OrgData } from '../../api';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import AddBatchProvidersModal from './onboardingStepModals/AddBatchProvidersModal';
import AddOfficesModal from './onboardingStepModals/AddOfficesModal';
import AddMembersModal from '../settings/AddMembersModal/AddMembersModal';
import gradientEhrIcon from 'design-system/src/assets/icons/gradientEhr.png';
import { SUPPORTED_EHR_NAMES, SupportedEhrName } from '../../types';
import { PracticeOnboardingStatuses } from '../../api';
import EhrIntegrationModal from './onboardingStepModals/EhrIntegrationModal';
import * as Sentry from '@sentry/react';
import gradientPhoneIcon from 'design-system/src/assets/icons/gradientPhone.png';
import gradientBellIcon from 'design-system/src/assets/icons/gradientBell.png';
import AddBatchPhoneNumbersModal from './onboardingStepModals/AddBatchPhoneNumbersModal';
import SendFirstPrescriptionModal from './onboardingStepModals/SendFirstPrescriptionModal';
import { getEhrDisplayName } from '../../utils';
import { NOTIFICATION_PREFERENCES_SETTINGS_PATH, ONBOARDING_STEP_COPY } from './onboardingStepDefinitions';
import { createTrackingProps } from 'shared-utils/analytics';

const STEP_CARD_WRAPPER_CLASS = 'flex flex-col [&>div]:flex-1';
const STEP_CARD_ICON_CLASS = 'size-[46px] object-contain';
const goToSettingsButtonClassName = '!border-grayscale-400 !bg-white !text-grayscale-900';

interface OnboardingStepsProps {
  isAdmin: boolean;
  practiceOnboardingStatuses: PracticeOnboardingStatuses;
}

type OnboardingStep = {
  key: string;
  isCompleted: boolean;
  show: boolean;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  primaryButton: (ButtonProps | RouterLinkProps) & { label: string };
};

type OnboardingModalType =
  | 'ehrIntegration'
  | 'addPhoneNumbers'
  | 'sendFirstPrescription'
  | 'addProviders'
  | 'addOffices'
  | 'addUsers'
  | null;

export const OnboardingSteps: React.FC<OnboardingStepsProps> = ({ isAdmin, practiceOnboardingStatuses }) => {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState<OnboardingModalType>(null);

  const { data: phoneNumbersData, isLoading: isLoadingPhoneNumbers } = useQuery({
    queryKey: ['office-phone-numbers'],
    queryFn: () => client.get('/settings/phone-numbers'),
  });

  const { data: orgMembersData } = useQuery<OrgData>({
    queryKey: ['orgMembers'],
    queryFn: () => client.get('org-members').then((res) => res.data),
  });

  const {
    data: ehrData,
    isLoading: isLoadingEhrName,
    isError: isErrorEhrName,
  } = useQuery({
    queryKey: ['ehrName'],
    queryFn: () => client.get(`/onboarding/ehr-name`).then((res) => res.data),
  });
  const supportedEhr = ehrData?.ehr && SUPPORTED_EHR_NAMES.includes(ehrData.ehr) ? ehrData.ehr : null;

  const hasEhrIntegration = practiceOnboardingStatuses.has_ehr_integration;
  const hasAddedPhoneNumbers = practiceOnboardingStatuses.has_added_phone_numbers;
  const hasQueuedPrescriptions = practiceOnboardingStatuses.has_queued_prescriptions;
  const hasSavedNotificationPreferences = practiceOnboardingStatuses.has_saved_notification_preferences;
  const hasInvitedTeammates = practiceOnboardingStatuses.has_invited_teammates;
  const hasAddedProviders = practiceOnboardingStatuses.has_added_providers;
  const hasAddedOffice = practiceOnboardingStatuses.has_added_office;

  const allSteps: OnboardingStep[] = useMemo(
    () => [
      {
        key: 'ehrIntegration',
        isCompleted: hasEhrIntegration,
        show: Boolean(
          supportedEhr &&
          (practiceOnboardingStatuses.ehr_integration_status === 'awaiting_credentials' || hasEhrIntegration),
        ),
        icon: <img src={gradientEhrIcon} aria-hidden={true} className={STEP_CARD_ICON_CLASS} />,
        title: supportedEhr ? `Connect to ${getEhrDisplayName(supportedEhr)}` : 'Connect to EHR',
        subtitle:
          'Tandem can automatically extract patient and insurance information so you can complete prior authorizations faster.',
        primaryButton: hasEhrIntegration
          ? {
              label: 'Go to Settings',
              onClick: () => navigate('/settings?tab=ehrIntegration'),
              ...createTrackingProps({ elementName: 'Onboarding Steps: Go to Settings: EHR Integration Tab Button' }),
              className: goToSettingsButtonClassName,
            }
          : {
              label: 'Connect',
              onClick: () => setModalOpen('ehrIntegration'),
              ...createTrackingProps({ elementName: 'Onboarding Steps: Connect EHR Button' }),
            },
      },
      {
        key: 'inviteTeammates',
        isCompleted: hasInvitedTeammates,
        show: isAdmin,
        icon: <img src={gradientPeopleIcon} aria-hidden={true} className={STEP_CARD_ICON_CLASS} />,
        title: 'Invite your teammates',
        subtitle: 'Bring your team into Tandem to manage prescriptions, enrollments, and PAs together.',
        primaryButton: hasInvitedTeammates
          ? {
              label: 'Go to Settings',
              onClick: () => navigate('/settings?tab=users'),
              ...createTrackingProps({ elementName: 'Onboarding Steps: Go to Settings: Users Tab Button' }),
              className: goToSettingsButtonClassName,
            }
          : {
              label: 'Add',
              onClick: () => setModalOpen('addUsers'),
              ...createTrackingProps({ elementName: 'Onboarding Steps: Invite Teammates Button' }),
            },
      },
      {
        key: 'addProviders',
        isCompleted: hasAddedProviders,
        show: isAdmin,
        icon: <img src={gradientProviderIcon} aria-hidden={true} className={STEP_CARD_ICON_CLASS} />,
        title: 'Add more providers',
        subtitle: "Enter each provider's National Provider Identifier (NPI) so we can match prescriptions correctly.",
        primaryButton: hasAddedProviders
          ? {
              label: 'Go to Settings',
              onClick: () => navigate('/settings?tab=providers'),
              ...createTrackingProps({ elementName: 'Onboarding Steps: Go to Settings: Providers Tab Button' }),
              className: goToSettingsButtonClassName,
            }
          : {
              label: 'Add',
              onClick: () => setModalOpen('addProviders'),
              ...createTrackingProps({ elementName: 'Onboarding Steps: Add Providers Button' }),
            },
      },
      {
        key: 'addOffice',
        isCompleted: hasAddedOffice,
        show: isAdmin,
        icon: <img src={gradientOfficeIcon} aria-hidden={true} className={STEP_CARD_ICON_CLASS} />,
        title: 'Add your office address',
        subtitle: 'Ensures materials arrive and prescriptions stay properly matched to your office.',
        primaryButton: hasAddedOffice
          ? {
              label: 'Go to Settings',
              onClick: () => navigate('/settings?tab=offices'),
              ...createTrackingProps({ elementName: 'Onboarding Steps: Go to Settings: Offices Tab Button' }),
              className: goToSettingsButtonClassName,
            }
          : {
              label: 'Add',
              onClick: () => setModalOpen('addOffices'),
              ...createTrackingProps({ elementName: 'Onboarding Steps: Add Office Address Button' }),
            },
      },
      {
        key: 'addPhoneNumbers',
        isCompleted: hasAddedPhoneNumbers,
        show: true,
        icon: <img src={gradientPhoneIcon} aria-hidden={true} className={STEP_CARD_ICON_CLASS} />,
        title: 'Add office numbers',
        subtitle: 'Add any phone numbers that may appear on prescriptions for accurate routing.',
        primaryButton: hasAddedPhoneNumbers
          ? {
              label: 'Go to Settings',
              onClick: () => navigate('/settings?tab=officeNumbers'),
              ...createTrackingProps({ elementName: 'Onboarding Steps: Go to Settings: Office Numbers Tab Button' }),
              className: goToSettingsButtonClassName,
            }
          : {
              label: 'Add',
              onClick: () => setModalOpen('addPhoneNumbers'),
              ...createTrackingProps({ elementName: 'Onboarding Steps: Add Office Numbers Button' }),
            },
      },
      {
        key: 'queuePrescriptions',
        isCompleted: hasQueuedPrescriptions,
        show: true,
        icon: <img src={gradientPrescriptionIcon} aria-hidden={true} className={STEP_CARD_ICON_CLASS} />,
        title: 'Queue up prescriptions',
        subtitle: "You can begin sending prescriptions now—we'll hold them until your account is verified.",
        primaryButton: hasQueuedPrescriptions
          ? {
              label: 'Learn more',
              onClick: () => navigate('/prescriptions'),
              ...createTrackingProps({ elementName: 'Onboarding Steps: Learn More: Queue Up Prescriptions Button' }),
              className: goToSettingsButtonClassName,
            }
          : {
              label: 'Get started',
              onClick: () => setModalOpen('sendFirstPrescription'),
              ...createTrackingProps({ elementName: 'Onboarding Steps: Queue Up Prescriptions Button' }),
            },
      },
      {
        key: 'notificationPreferences',
        isCompleted: hasSavedNotificationPreferences,
        show: true,
        icon: <img src={gradientBellIcon} aria-hidden={true} className={STEP_CARD_ICON_CLASS} />,
        title: ONBOARDING_STEP_COPY.notificationPreferences.title,
        subtitle: ONBOARDING_STEP_COPY.notificationPreferences.description,
        primaryButton: hasSavedNotificationPreferences
          ? {
              label: 'Go to Settings',
              onClick: () => navigate(NOTIFICATION_PREFERENCES_SETTINGS_PATH),
              ...createTrackingProps({
                elementName: 'Onboarding Steps: Go to Settings: Notification Preferences Tab Button',
              }),
              className: goToSettingsButtonClassName,
            }
          : {
              label: 'Set up',
              onClick: () => navigate(NOTIFICATION_PREFERENCES_SETTINGS_PATH),
              ...createTrackingProps({ elementName: 'Onboarding Steps: Set Up Notification Preferences Button' }),
            },
      },
    ],
    [
      hasEhrIntegration,
      hasInvitedTeammates,
      hasAddedProviders,
      hasAddedOffice,
      hasAddedPhoneNumbers,
      hasQueuedPrescriptions,
      hasSavedNotificationPreferences,
      supportedEhr,
      practiceOnboardingStatuses.ehr_integration_status,
      isAdmin,
      navigate,
    ],
  );

  // Filter and sort: show only relevant steps, incomplete first
  const visibleSteps = allSteps
    .filter((step) => step.show)
    .sort((a, b) => {
      if (a.isCompleted === b.isCompleted) return 0;
      return a.isCompleted ? 1 : -1;
    });

  // Report but don't block onboarding page on this error
  useEffect(() => {
    if (isErrorEhrName) {
      Sentry.captureException(new Error('Error loading EHR name'));
    }
  }, [isErrorEhrName]);

  if (isLoadingEhrName) {
    return <LoadingSpinner height="600px" />;
  }

  return (
    <div className="flex flex-col gap-4">
      <Typography styledAs="h6" renderedAs="h2">
        Get started with Tandem
      </Typography>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {visibleSteps.map((step) => (
          <div key={step.key} className={STEP_CARD_WRAPPER_CLASS}>
            <DynamicStepCard
              icon={step.icon}
              title={step.title}
              subtitle={step.subtitle}
              isCompleted={step.isCompleted}
              primaryButton={step.primaryButton}
            />
          </div>
        ))}
      </div>
      {supportedEhr && (
        <EhrIntegrationModal
          open={modalOpen === 'ehrIntegration'}
          onClose={() => setModalOpen(null)}
          ehr={supportedEhr as SupportedEhrName}
        />
      )}
      <AddBatchPhoneNumbersModal
        open={modalOpen === 'addPhoneNumbers'}
        onClose={() => setModalOpen(null)}
        existingNumbers={phoneNumbersData?.data?.phone_numbers || []}
        isLoading={isLoadingPhoneNumbers}
      />
      <SendFirstPrescriptionModal open={modalOpen === 'sendFirstPrescription'} onClose={() => setModalOpen(null)} />
      <AddBatchProvidersModal open={modalOpen === 'addProviders'} onClose={() => setModalOpen(null)} />
      <AddOfficesModal open={modalOpen === 'addOffices'} onClose={() => setModalOpen(null)} />
      {modalOpen === 'addUsers' && (
        <AddMembersModal onClose={() => setModalOpen(null)} members={orgMembersData?.members ?? []} showAddedUsers />
      )}
    </div>
  );
};
